export interface LearningCardGridSkeletonProps {
  /** Placeholder count. Six fills three columns twice over at `lg`. */
  count?: number;
}

/**
 * Cold-fetch placeholder shown on (re)load before any lesson data lands.
 *
 * Mirrors `LearningCard`'s footprint exactly — same grid (auto-rows +
 * breakpoints), same card chrome, same internal rhythm (title / 2-line body /
 * tag row / footer with version chip + button) — so when real data arrives the
 * cards resolve in place with zero reflow. A lesson that is still generating
 * then settles into `LearningCard`'s own "Creating lesson…" state, which reuses
 * these same shimmer bars.
 *
 * `motion-reduce:animate-none` on every bar, not the container: the shimmer is
 * decorative and there is nothing else to convey loading to someone who has
 * reduced motion on.
 */
export function LearningCardGridSkeleton({
  count = 6,
}: LearningCardGridSkeletonProps) {
  return (
    <div className='mb-8 grid auto-rows-[minmax(250px,_auto)] grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className='flex h-full flex-col rounded-xl border border-border/60 bg-card/70 p-6 backdrop-blur-sm dark:bg-card'
        >
          {/* title */}
          <div className='mb-3 h-6 w-3/4 animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          {/* description (2 lines) */}
          <div className='mb-2 h-4 w-full animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          <div className='mb-5 h-4 w-2/3 animate-pulse rounded-sm bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          {/* tags */}
          <div className='mb-6 flex gap-1.5'>
            <div className='h-6 w-16 animate-pulse rounded-md bg-secondary motion-reduce:animate-none dark:bg-white/10' />
            <div className='h-6 w-20 animate-pulse rounded-md bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          </div>
          {/* footer (version chip + CTA block) */}
          <div className='mt-auto flex items-center justify-between'>
            <div className='h-5 w-12 animate-pulse rounded-md bg-secondary motion-reduce:animate-none dark:bg-white/10' />
            <div className='h-8 w-28 animate-pulse rounded-md bg-secondary motion-reduce:animate-none dark:bg-white/10' />
          </div>
        </div>
      ))}
    </div>
  );
}
